import { parseURL } from 'ufo';

import { getPublicRuntimeConfig } from '@/config/runtime';

export interface CorporateLogoOptions {
    theme?: 'dark' | 'light';
}

export interface CorporateLogoIdentity {
    name?: string;
    ticker?: string;
}

export type CorporateLogoTheme = NonNullable<CorporateLogoOptions['theme']>;

const normalizeCorporateLogoTheme = (themeName?: string | null): CorporateLogoTheme | undefined => {
    if (!themeName) {
        return undefined;
    }

    return themeName.toLocaleLowerCase().includes('dark') ? 'dark' : 'light';
};

/**
 * Resolve Logo.dev's image theme from the same persisted/resolved theme used by
 * the platform. Keeping this at the URL boundary prevents individual avatar
 * call sites from drifting between themed and unthemed requests.
 */
export const getCorporateLogoTheme = (): CorporateLogoTheme => {
    if (typeof window !== 'undefined') {
        try {
            const storedTheme = window.localStorage.getItem('themeName');

            if (storedTheme === 'system') {
                return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            }

            const resolvedTheme = normalizeCorporateLogoTheme(storedTheme);

            if (resolvedTheme) {
                return resolvedTheme;
            }
        } catch {
            // Storage can be unavailable in privacy-restricted browser contexts.
        }

        // The provider persists the next selection before its DOM synchronization
        // effect runs. Reading storage first keeps Logo.dev URLs correct during the
        // same render that changes the application theme.
        if (typeof document !== 'undefined') {
            const resolvedTheme = normalizeCorporateLogoTheme(document.documentElement.dataset.appTheme);

            if (resolvedTheme) {
                return resolvedTheme;
            }
        }

        if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
    } else if (typeof document !== 'undefined') {
        const resolvedTheme = normalizeCorporateLogoTheme(document.documentElement.dataset.appTheme);

        if (resolvedTheme) {
            return resolvedTheme;
        }
    }

    return 'light';
};

export const themedCorporateLogoUrl = (
    logoUrl: string | undefined,
    theme: CorporateLogoTheme = getCorporateLogoTheme(),
): string | undefined => {
    if (!logoUrl?.startsWith('https://img.logo.dev/')) {
        return logoUrl;
    }

    if (/([?&])theme=/.test(logoUrl)) {
        return logoUrl.replace(/([?&])theme=[^&]+/, `$1theme=${theme}`);
    }

    return `${logoUrl}${logoUrl.includes('?') ? '&' : '?'}theme=${theme}`;
};

export const isLogoDevLogoUrl = (logoUrl?: string): boolean => {
    if (!logoUrl) {
        return false;
    }

    try {
        return new URL(logoUrl).hostname.toLocaleLowerCase() === 'img.logo.dev';
    } catch {
        return false;
    }
};

export const corporateLogo = (website: string, options: CorporateLogoOptions = {}): string | undefined => {
    const normalizedWebsite = website.trim();

    if (isLogoDevLogoUrl(normalizedWebsite)) {
        return themedCorporateLogoUrl(normalizedWebsite, options.theme ?? getCorporateLogoTheme());
    }

    const value = parseURL(
        /^[a-z][a-z\d+.-]*:\/\//i.test(normalizedWebsite) || normalizedWebsite.startsWith('//')
            ? normalizedWebsite
            : `https://${normalizedWebsite}`,
    ).host;
    const apiKey = getPublicRuntimeConfig().logoDevApiKey;

    if (!value || !apiKey) {
        return undefined;
    }

    return themedCorporateLogoUrl(
        `https://img.logo.dev/${value.replace(/^www\./i, '').toLocaleLowerCase()}?token=${apiKey}&size=768&format=png`,
        options.theme ?? getCorporateLogoTheme(),
    );
};

const corporateLogoByIdentifier = (
    identifierType: 'name' | 'ticker',
    identifier: string | undefined,
    options: CorporateLogoOptions = {},
): string | undefined => {
    const normalizedIdentifier = identifier?.trim();
    const apiKey = getPublicRuntimeConfig().logoDevApiKey;

    if (!normalizedIdentifier || !apiKey) {
        return undefined;
    }

    const lookupValue = identifierType === 'ticker' ? normalizedIdentifier.toLocaleUpperCase() : normalizedIdentifier;

    return themedCorporateLogoUrl(
        `https://img.logo.dev/${identifierType}/${encodeURIComponent(lookupValue)}?token=${apiKey}&size=768&format=png`,
        options.theme ?? getCorporateLogoTheme(),
    );
};

export const corporateLogoByTicker = (ticker: string, options: CorporateLogoOptions = {}) =>
    corporateLogoByIdentifier('ticker', ticker, options);

export const corporateLogoByName = (name: string, options: CorporateLogoOptions = {}) =>
    corporateLogoByIdentifier('name', name, options);

/**
 * Resolve a corporate avatar without allowing a legacy vendor image to win.
 * A known company website is converted into the canonical Logo.dev image URL;
 * provider candidates are accepted only when they are already served by Logo.dev.
 */
export const resolveCorporateLogo = (
    website?: string,
    candidateLogoUrl?: string,
    options: CorporateLogoOptions = {},
    identity: CorporateLogoIdentity = {},
): string | undefined => {
    const generatedLogo = website ? corporateLogo(website, options) : undefined;

    if (generatedLogo) {
        return generatedLogo;
    }

    if (isLogoDevLogoUrl(candidateLogoUrl)) {
        return themedCorporateLogoUrl(candidateLogoUrl, options.theme ?? getCorporateLogoTheme());
    }

    return (
        corporateLogoByIdentifier('ticker', identity.ticker, options) ??
        corporateLogoByIdentifier('name', identity.name, options)
    );
};
